import { useContext } from 'react';
import { motion, useTransform, cubicBezier, useAnimate } from 'framer-motion';
import { useSanitizedTranslation, useInViewScroll, MotionImage } from 'ui';
import { PageContext } from 'ui/contexts';
import { StackedContent } from '../StackedContent';

const creditsLogos = [
  {
    alt: 'Apple TV',
    height: 45,
    src: `/static/images/refresh/2026/credits/images/small_logos/Apple_TV_Logo_Wht_CI_082925 1.png`,
    width: 88,
  },
  {
    alt: 'HBO Max',
    height: 57,
    src: `/static/images/refresh/2026/credits/images/small_logos/HBO Max_Logo_Pure White_RGB_052225 2.png`,
    width: 78,
  },
  {
    alt: 'Peacock',
    height: 35,
    src: `/static/images/refresh/2026/credits/images/small_logos/peacock-logo-bw-white 1.png`,
    width: 146,
  },
  {
    alt: 'AMC Plus',
    height: 32,
    src: `/static/images/refresh/2026/credits/images/small_logos/AMCPLUS_Logo_white_RGB 1.svg`,
    width: 103,
  },
  {
    alt: 'MGM Plus',
    height: 90,
    src: `/static/images/refresh/2026/credits/images/small_logos/MGMPlus_WHITE_RGB 1.png`,
    width: 129,
  },
];

export interface EventsHeroProps {
  type?: 'credits';
  bgColor?: string;
  textColor?: string;
  eyebrow?: any;
  title: string;
  body?: any[];
  cta?: any;
  imageSrc?: string;
}

export const EventsHero = ({ type, bgColor = 'white', textColor = 'xumoDarkPlum', eyebrow, title, body, cta, imageSrc }: EventsHeroProps) => {
  const { t } = useSanitizedTranslation();

  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 0px', 'end 50vh']);
  const overlayOpacity = useTransform(amt, [0.5, 0.8], [0, 1.0], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const swirlY = useTransform(amt, [0, 0.35], ['-5.75%', '4.5%'], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const titleY = useTransform(amt, [0, 0.5], [0, -75]);
  const titleScale = useTransform(amt, [0, 0.5], [1, 0.9]);

  const { headerHeight } = useContext(PageContext);

  const [scope, animate] = useAnimate();

  // Credits Page Version
  if (type === 'credits') {
    return (
      <section ref={ref} className="w-full bg-white text-xumoDarkPlum lg:h-[calc(100svh-89px)] lg:min-h-[1300px]" data-testid="text-hero-section">
        <motion.div ref={scope} className="flex h-full flex-col items-center pt-6 md:pt-8 lg:pt-12">
          <MotionImage
            priority
            style={{ y: titleY, scale: titleScale }}
            height={41}
            width={237}
            className="logo-img mx-auto mb-8 h-[28px] w-auto sm:h-[36px]"
            src="/static/images/refresh/2026/credits/xumo-credits-logo.png"
            alt=""
          />

          {/* Main hero title: "It pays to stream / with Xumo" */}
          <motion.h1 style={{ y: titleY, scale: titleScale }} className="type-title text-center" data-i18n dangerouslySetInnerHTML={{ __html: t(title) }} />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, ease: [0, 1, 1, 1], duration: 0.9, deltaRest: 0.00001, deltaSpeed: 0.00001 }}
            className="relative -mt-4 grid h-[650px] w-full max-w-[2040px] overflow-hidden bg-[url('/static/images/refresh/2026/credits/images/mobile-hero.webp')] bg-cover bg-top text-white sm:h-[700px] md:h-[900px] lg:min-h-0 lg:flex-1 lg:bg-[url('/static/images/refresh/2026/credits/images/credits-hero-improved.webp')] lg:bg-center"
          >
            {/* Gradient overlays */}
            <div className="pointer-events-none absolute inset-y-0 -left-6 z-20 hidden w-12 bg-gradient-to-r from-white via-white/60 to-transparent min-[2040px]:block" />
            <div className="pointer-events-none absolute inset-y-0 -right-6 z-20 hidden w-12 bg-gradient-to-l from-white via-white/40 to-transparent min-[2040px]:block" />

            {/* Background image panel: Hero.png swirl artwork */}
            <div className="mx-auto flex w-full flex-col items-center justify-end gap-8 px-2 pb-4 sm:px-8 sm:pb-8 lg:flex-row lg:justify-center lg:pt-14">
              {/* Promo headline, body copy, and app logos */}
              <div className="mt-0 max-w-[1100px] text-center lg:w-[60%] xl:w-2/3">
                {/* Promo headline */}
                <h2 className="font-WorkSans text-[85px] leading-none tracking-tighter text-white sm:text-[85px] md:text-[150px] lg:mt-[230px] lg:text-[150px] xl:text-[200px] 2xl:text-[230px]">
                  Get $100
                </h2>
                {/* Desktop promo body text */}
                <p className="type-heading-alt mt-4 hidden text-white lg:block">
                  <strong>in Xumo Credits</strong> to watch
                  <br />
                  what you really want
                </p>

                {/* Desktop app logo images */}
                <div className="mt-8 hidden w-full flex-nowrap items-center justify-between lg:flex xl:mt-16 ">
                  {creditsLogos.map(logo => (
                    <MotionImage key={logo.src} src={logo.src} alt={logo.alt} width={logo.width} height={logo.height} className="object-contain" />
                  ))}
                </div>
              </div>

              {/* Product image: Xumo Stream Box and remote */}
              <div className="flex w-[200px] flex-col items-center md:w-[270px] lg:mb-8 lg:w-[40%] lg:max-w-none lg:items-start xl:w-1/3">
                <MotionImage
                  priority
                  src="/static/images/refresh/2026/credits/images/xumo_remote.png"
                  alt="Xumo Stream Box and remote"
                  width={1085}
                  height={755}
                  className="object-contain"
                />
              </div>

              {/* Mobile promo body text and app logos */}
              <div className="w-full text-center lg:hidden">
                <p className="type-heading-alt text-white">
                  <strong>in Xumo Credits</strong> to watch
                  <br />
                  what you really want
                </p>

                <div className="mt-8 flex w-full flex-nowrap items-center justify-between px-5">
                  {creditsLogos.map(logo => (
                    <MotionImage
                      key={logo.src}
                      src={logo.src}
                      alt={logo.alt}
                      width={logo.width}
                      height={logo.height}
                      className="h-auto w-auto shrink-0 object-contain "
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Limited time offer strip */}
          <div className="relative w-full max-w-[2040px] overflow-hidden bg-gradient-to-r from-[#eed9f7] via-[#f6edfb] to-[#d9f8fb] py-2 lg:py-4">
            <p className="text-center text-[16px] font-bold uppercase leading-tight tracking-[0.2em] text-[#8A20D5] md:text-sm lg:text-lg">
              Limited time offer, ends 8/31/2026
            </p>
          </div>
        </motion.div>
      </section>
    );
  }
  // End Credits Page Version

  return (
    <>
      <section ref={ref} className={`relative w-full bg-${bgColor} text-${textColor}`} data-testid={'text-hero-section'}>
        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          ref={scope}
          className="wrapper z-20 flex h-full min-h-[calc(100vh-8rem)] flex-col items-center pb-24 pt-16 lg:py-16"
        >
          <motion.h1
            style={{ y: titleY, scale: titleScale }}
            className="type-heading-alt mb-8 text-center lg:mb-12"
            data-i18n
            dangerouslySetInnerHTML={{ __html: t(eyebrow) }}
          />

          <motion.h1
            style={{ y: titleY, scale: titleScale }}
            className="type-title-alt mb-8 text-center lg:mb-16 [&>*]:inline-block"
            data-i18n
            dangerouslySetInnerHTML={{ __html: t(title) }}
          />

          <motion.div style={{ y: titleY, scale: titleScale }} className="text-center">
            {body?.map((item: Object, index: number) => (
              <p key={index} dangerouslySetInnerHTML={{ __html: t(Object.values(item)[0]) }} className={Object.values(item)[1]} />
            ))}
          </motion.div>

          {cta && (
            <motion.div style={{ y: titleY, scale: titleScale }} className="relative z-20 mt-8 flex justify-center lg:pb-12">
              <StackedContent content={cta} center />
            </motion.div>
          )}

          {/* Swirl */}
          <motion.div
            initial={{ opacity: 0, marginTop: 30 }}
            animate={{ opacity: 1, marginTop: 0 }}
            transition={{ delay: 0.75, ease: [0, 1, 1, 1], duration: 0.9, deltaRest: 0.00001, deltaSpeed: 0.00001 }}
            className="relative mt-8 w-screen max-w-[2040px] md:-mt-[30px] lg:-mt-[80px] xl:-mt-[100px] 2xl:-mt-[110px]"
          >
            <MotionImage
              priority
              src={imageSrc || '/static/images/refresh/2025/ces/hero.webp'}
              alt=""
              width={2560} // actual image width
              height={1265} // actual image height
              className="h-auto w-full object-contain"
              style={{ opacity: 1, y: swirlY }}
            />
          </motion.div>

          {/* Dradient overlay */}
          <motion.div
            initial={{ opacity: 0, marginTop: 30 }}
            animate={{ opacity: 1, marginTop: 0 }}
            transition={{ delay: 0.75, ease: [0, 1, 1, 1], duration: 0.9, deltaRest: 0.00001, deltaSpeed: 0.00001 }}
          >
            <div
              style={{
                display: 'none',
              }}
              className="pointer-events-none absolute left-1/2 top-0 z-40 mx-auto h-full w-[2040px] -translate-x-1/2"
              aria-hidden
            >
              <style jsx>{`
                @media (min-width: 2040px) {
                  div[aria-hidden] {
                    display: block !important;
                  }
                }
              `}</style>{' '}
              <div className="pointer-events-none absolute left-0 top-0 h-full w-[100px] bg-gradient-to-r from-xumoDarkPlum via-xumoDarkPlum/40 to-transparent" />
              <div className="pointer-events-none absolute right-0 top-0 h-full w-[100px] bg-gradient-to-l from-xumoDarkPlum via-xumoDarkPlum/40 to-transparent" />
            </div>
          </motion.div>
        </motion.div>
      </section>
    </>
  );
};
